'use client';

/**
 * Merchant-facing support address card.
 *
 * Shows the address their storefront advertises (`care@<slug>.tgdcare.com`
 * once verified) and lets them bring their own domain. Tagada still answers
 * the mail — only the identity changes.
 */

import * as React from 'react';
import { Check, Copy, Loader2, Mail, RefreshCw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import {
  useRxCheckSupportMailbox,
  useRxRemoveCustomSupportDomain,
  useRxRequestCustomSupportDomain,
  useRxSupportMailbox,
} from '@/lib/api/rx';

type DnsRecord = {
  record?: string;
  name: string;
  type: string;
  value: string;
  priority?: number;
};

export function RxSupportMailboxCard({ storeId, brandName }: { storeId: string; brandName: string }) {
  const { toast } = useToast();
  const mailbox = useRxSupportMailbox(storeId);
  const requestCustom = useRxRequestCustomSupportDomain();
  const checkMailbox = useRxCheckSupportMailbox();
  const removeCustom = useRxRemoveCustomSupportDomain();

  const [domain, setDomain] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  const advertised = mailbox.data?.advertised;
  const active = mailbox.data?.active;
  const pending = mailbox.data?.pendingCustom;
  const shown = pending ?? active;
  const records = (shown?.dnsRecords ?? []) as DnsRecord[];

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const onRequest = async () => {
    try {
      await requestCustom.mutateAsync({
        storeId,
        domain: domain.trim(),
        displayName: `${brandName || 'Care'} Care`,
      });
      toast({ title: 'DNS records ready', description: 'Add them at your registrar, then click Check.' });
      setDomain('');
      await mailbox.refetch();
    } catch (err) {
      toast({
        title: 'Could not register this domain',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const onCheck = async (mailboxId: string) => {
    try {
      const row = await checkMailbox.mutateAsync({ storeId, mailboxId });
      toast({
        title: 'DNS checked',
        description: `Send: ${row.domainStatus} · Receive: ${row.receivingStatus}`,
      });
      await mailbox.refetch();
    } catch (err) {
      toast({
        title: 'Check failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const onRemove = async (mailboxId: string) => {
    try {
      await removeCustom.mutateAsync({ storeId, mailboxId });
      toast({ title: 'Custom domain removed', description: 'Patients will write to the tgdcare.com address again.' });
      await mailbox.refetch();
    } catch (err) {
      toast({
        title: 'Could not remove domain',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Patient support address
        </CardTitle>
        <CardDescription>
          Patients write here. Tagada answers as your brand — you do not handle the inbox.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {mailbox.isLoading ? (
          <p className="text-muted-foreground text-sm">Loading mailbox…</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <code className="bg-muted rounded px-2 py-1 text-sm">{advertised?.address ?? '—'}</code>
              {advertised?.address ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => void copy(advertised.address)}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              ) : null}
              {advertised?.verified ? (
                <Badge variant="secondary">receiving</Badge>
              ) : (
                <Badge variant="outline">not live yet</Badge>
              )}
            </div>
            {!advertised?.verified ? (
              <p className="text-muted-foreground text-sm">
                Publish the storefront to provision <code>care@your-slug.tgdcare.com</code>. Until then the
                page shows the platform fallback.
              </p>
            ) : null}

            {shown && shown.kind === 'custom_domain' ? (
              <div className="space-y-3 rounded-lg border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">Your domain</p>
                    <p className="text-muted-foreground font-mono text-xs">{shown.address}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={checkMailbox.isPending}
                      onClick={() => void onCheck(shown.id)}
                    >
                      {checkMailbox.isPending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Check DNS
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={removeCustom.isPending}
                      onClick={() => void onRemove(shown.id)}
                    >
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                      Remove
                    </Button>
                  </div>
                </div>
                {shown.receivingStatus !== 'verified' && records.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="text-muted-foreground">
                          <th className="py-1 pr-3 font-medium">Type</th>
                          <th className="py-1 pr-3 font-medium">Name</th>
                          <th className="py-1 font-medium">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records
                          .filter((r) => r.record?.toLowerCase() !== 'tracking')
                          .map((r) => (
                            <tr key={`${r.type}-${r.name}-${r.value}`} className="align-top">
                              <td className="py-1 pr-3 font-mono">{r.type}</td>
                              <td className="py-1 pr-3 font-mono">{r.name || '@'}</td>
                              <td className="py-1 break-all font-mono">
                                {r.type === 'MX' && r.priority != null ? `${r.priority} ${r.value}` : r.value}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    <p className="text-muted-foreground mt-2">
                      Add these records at your DNS host. If this domain already receives mail, use a
                      dedicated subdomain (e.g. <code>help.yourbrand.com</code>) so we never take over your inbox.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="rx-support-domain">Use your own domain</Label>
                <div className="flex flex-wrap gap-2">
                  <Input
                    id="rx-support-domain"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="help.yourbrand.com"
                    className="max-w-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!domain.trim() || requestCustom.isPending}
                    onClick={() => void onRequest()}
                  >
                    {requestCustom.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Request records
                  </Button>
                </div>
                <p className="text-muted-foreground text-xs">
                  Address becomes <code>care@&lt;domain&gt;</code>. We still handle support — only the
                  address patients see changes.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
